/**
 * Shared presentation helpers for call scorecard records. Kept pure so the
 * Scorecard page and the ticker-detail section agree on wording and color,
 * and so they can be unit tested without rendering.
 */

/** Human label for a call's state. Open calls with no levels are "tracking" (return-only). */
export const outcomeLabel = (call) => {
  if (!call) return '';
  if (call.status !== 'closed' || !call.outcome) {
    return call.hasLevels ? 'Open' : 'Tracking';
  }
  switch (call.outcome.result) {
    case 'target_hit':        return 'Target hit';
    case 'partial_then_stop': return 'Partial, then stopped';
    case 'stop_hit':          return 'Stopped out';
    case 'superseded':        return 'Superseded';
    default:                  return call.outcome.result || 'Closed';
  }
};

/** Badge class for the outcome: green for wins, red for losses, neutral otherwise. */
export const outcomeBadge = (call) => {
  if (!call || call.status !== 'closed' || !call.outcome) return 'badge-info';
  if (call.outcome.isWin === true)  return 'badge-success';
  if (call.outcome.isWin === false) return 'badge-danger';
  return 'badge-warning';
};

/** The return that best describes the call right now: realized when closed, mark-to-market when open. */
export const effectiveReturn = (call) => {
  if (!call) return null;
  if (call.status === 'closed' && call.outcome && call.outcome.returnPct != null) return call.outcome.returnPct;
  if (call.currentReturnPct != null) return call.currentReturnPct;
  return null;
};

export const returnColor = (pct) => {
  if (pct == null) return 'var(--text-secondary)';
  if (pct > 0) return 'var(--success-color)';
  if (pct < 0) return 'var(--danger-color)';
  return 'var(--text-secondary)';
};

export const formatPct = (pct, digits = 1) => {
  if (pct == null || Number.isNaN(Number(pct))) return '—';
  const n = Number(pct);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}%`;
};

export const formatPrice = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const digits = v >= 1000 ? 0 : v >= 1 ? 2 : 4;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

/** "95–97" for a range, "95" for a single price, "" when neither is set. */
export const formatEntry = (call) => {
  if (!call) return '';
  const { entryLow, entryHigh } = call;
  if (entryLow == null && entryHigh == null) return '';
  if (entryLow == null || entryHigh == null || entryLow === entryHigh) return formatPrice(entryLow ?? entryHigh);
  return `${formatPrice(entryLow)}–${formatPrice(entryHigh)}`;
};

export const kindLabel = (kind) => (kind === 'guidance' ? 'Report guidance' : "Russ's plan");

export const horizonLabel = (horizon) => {
  if (horizon === 'short') return 'short-term';
  if (horizon === 'long')  return 'long-term';
  return '';
};

/** Days between the reference date and the close (or now, for open calls). */
export const daysOpen = (call, now = new Date()) => {
  if (!call) return null;
  const start = call.referenceDate || call.openedAt;
  if (!start) return null;
  const end = call.status === 'closed' && call.outcome ? call.outcome.closedAt : now;
  const ms = new Date(end) - new Date(start);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.round(ms / 86400000));
};
